import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorAbortHandler, formatErrorMessage } from '../../utils';
import { notificationAlert } from '../notifications';
import { cleanAccountsTableRow } from './account.slice';
import { accountStoreKey } from './account.const';
import {
  CalculateMarginAccountData,
  CalculateMarginAccountResult,
  CalculateMarginProjectData,
  CalculateMarginProjectResult,
  GetAccountData,
  PLEASE_FILL_OUT_ALL_FIELDS_WITH_PROPER_VALUES_ERROR_MESSAGE,
} from './account.types';
import axios from 'axios';
import { apiEndpoints } from '../../constants/api';
import { MarginProject, ProjectDetail } from '../../types';
import { UsersOnProjectStatsResponse } from '../accounts';
import {
  getAccountDummyMembers,
  getAccountDummyProjects,
  getDateFilterValue,
  getSelectedAccount,
  getSelectedAccountMembers,
  getSelectedProject,
} from './account.selectors';
import { RootState } from '../store';
import { isNil } from 'lodash';

export const fetchAccount = createAsyncThunk(
  `${accountStoreKey}/fetchAccount`,
  async (
    { accountId, fetchAllMembers, periodFiltering }: GetAccountData,
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${apiEndpoints.accounts()}/${accountId}`,
        { fetchAllMembers, periodFiltering },
      );
      const account = response.data.account;
      account.grossMarginTargets = response.data.grossMarginTargets;
      account.netMarginTargets = response.data.netMarginTargets;
      account.grossMarginHighlights = response.data.grossMarginHighlights;
      account.netMarginHighlights = response.data.netMarginHighlights;
      account.operationalCost = response.data.operationalCost;

      account.projects = account.projects.map((proj: ProjectDetail) => {
        proj.grossMarginTargets = response.data.grossMarginTargets;
        proj.netMarginTargets = response.data.netMarginTargets;
        proj.grossMarginHighlights = response.data.grossMarginHighlights;
        proj.netMarginHighlights = response.data.netMarginHighlights;
        return proj;
      });
      return account;
    } catch (err) {
      const errMessage = periodFiltering
        ? 'No SoWs active on this day :('
        : formatErrorMessage(err, 'Fetching account has failed');

      thunkAPI.dispatch(
        notificationAlert(errMessage, {
          variant: 'error',
        }),
      );

      periodFiltering &&
        thunkAPI.dispatch(cleanAccountsTableRow({ errMessage }));
      throw new Error(String(err));
    }
  },
);

export const updateProject = createAsyncThunk(
  `${accountStoreKey}/updateProject`,
  async (
    {
      projectId,
      projectedRevenueEndDate,
    }: {
      projectId: string;
      projectedRevenueEndDate: string;
    },
    thunkAPI,
  ) => {
    return await errorAbortHandler(
      async () => {
        const response = await axios.put(apiEndpoints.editProject(), {
          projectId,
          projectedRevenueEndDate,
        });
        return response.data as {
          project: ProjectDetail;
          financialStats: {
            projectedRevenue: number;
          };
        };
      },
      {
        signal: thunkAPI.signal,
        dispatch: thunkAPI.dispatch,
      },
    );
  },
);

export const calculateMargin = createAsyncThunk(
  `${accountStoreKey}/calculateMargin`,
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const accountDummyMembers = getAccountDummyMembers(state);
      const accountDummyProjects = getAccountDummyProjects(state);
      const accountMembers = getSelectedAccountMembers(state);
      const project = getSelectedProject(state);
      const account = getSelectedAccount(state);
      const dateFilterValue = getDateFilterValue(state);

      let areAllFieldsPresent = true;
      const dummyMembers = [];
      for (const filteredDummyMember of accountDummyMembers ?? []) {
        if (filteredDummyMember.country) {
          if (
            isNil(filteredDummyMember.compensationRate) ||
            isNil(filteredDummyMember.billRate) ||
            isNil(filteredDummyMember.allocation)
          ) {
            areAllFieldsPresent = false;
            break;
          }

          const dummyMemberObj: any = {
            id: filteredDummyMember.id,
            compensationRate: filteredDummyMember.compensationRate ?? 0,
            resource: { billRate: filteredDummyMember.billRate ?? 0 },
            allocation: filteredDummyMember.allocation ?? 100,
            legalLocation: filteredDummyMember.country,
            projectId: filteredDummyMember.projectId,
          };

          if (filteredDummyMember.isHidden) {
            dummyMemberObj.compensationRate = 0;
            dummyMemberObj.resource = { billRate: 0 };
            dummyMemberObj.isHidden = true;
          }

          dummyMembers.push(dummyMemberObj);
        }
      }

      const prProjects = [];
      for (const byProjectGroup of accountMembers) {
        const contractors = [];
        for (const member of byProjectGroup.members) {
          if (
            isNil(member.editable?.billRate) ||
            isNil(member.editable?.allocation)
          ) {
            areAllFieldsPresent = false;
            break;
          }

          const contractorObj: any = {
            id: member.id,
            compensationRate: member.editable?.compensationRate ?? 0,
            resource: { billRate: member.editable?.billRate ?? 0 },
            allocation: member.editable?.allocation ?? 100,
            legalLocation: member.legalLocation,
          };

          if (member.editable?.isHidden) {
            contractorObj.compensationRate = 0;
            contractorObj.resource = { billRate: 0 };
            contractorObj.isHidden = true;
          }

          contractors.push(contractorObj);
        }

        if (!areAllFieldsPresent) {
          break;
        }

        prProjects.push({
          id: byProjectGroup.projectId ?? '',
          contractors,
        });
      }

      if (!areAllFieldsPresent) {
        throw new Error(
          PLEASE_FILL_OUT_ALL_FIELDS_WITH_PROPER_VALUES_ERROR_MESSAGE,
        );
      }

      const projects: MarginProject[] = prProjects.concat(
        accountDummyProjects?.map((dummyProject) => ({
          id: dummyProject.id,
          contractors: [],
        })) ?? [],
      );

      if (dummyMembers?.length) {
        dummyMembers.forEach((dummyMember) => {
          projects.forEach((pr) => {
            if (pr.id === dummyMember.projectId) {
              pr.contractors.push(dummyMember);
            }
          });
        });
      }

      if (project) {
        const foundProject = projects.find((pr) => pr.id === project.id);
        const projectContractors = foundProject?.contractors ?? [];

        const endpoint = apiEndpoints.projectMargins(project.id);
        const response = await axios.post<CalculateMarginProjectResult>(
          endpoint,
          <CalculateMarginProjectData>{
            id: project.id,
            contractors: projectContractors,
            ...(dateFilterValue && {
              periodFiltering: {
                from: dateFilterValue,
                to: dateFilterValue,
              },
            }),
          },
        );

        return response.data as CalculateMarginProjectResult;
      } else {
        if (!account) return;

        const endpoint = apiEndpoints.accountMargins(account.id);
        const response = await axios.post<CalculateMarginAccountResult>(
          endpoint,
          <CalculateMarginAccountData>{
            id: account.id,
            projects,
            ...(dateFilterValue && {
              periodFiltering: {
                from: dateFilterValue,
                to: dateFilterValue,
              },
            }),
          },
        );

        return response.data as CalculateMarginAccountResult;
      }
    } catch (err) {
      if (
        !(err as Error).message?.includes(
          PLEASE_FILL_OUT_ALL_FIELDS_WITH_PROPER_VALUES_ERROR_MESSAGE,
        )
      ) {
        thunkAPI.dispatch(
          notificationAlert(formatErrorMessage(err, 'Invalid fields'), {
            variant: 'error',
          }),
        );
      }

      throw new Error(String(err));
    }
  },
);

export const fetchCompensationRange = createAsyncThunk(
  `${accountStoreKey}/fetchCompensationRange`,
  async (
    data: {
      seniority: string;
      stream: string;
      legalLocation: string;
      employmentType?: string;
      memberId: string;
    },
    thunkAPI,
  ) => {
    try {
      // TODO return real account data when API is ready
      const endpoint = apiEndpoints.fetchCompensationRange();
      const response = await axios.post(endpoint, {
        seniority: data.seniority,
        stream: data.stream,
        legalLocation: data.legalLocation,
        employmentType: data.employmentType,
      });

      return { data: response.data, memberId: data.memberId };
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(
          formatErrorMessage(err, 'Fetching accounts has failed'),
          {
            variant: 'error',
          },
        ),
      );
      throw new Error(String(err));
    }
  },
);

export const fetchUsersOnProjectStats = createAsyncThunk(
  `${accountStoreKey}/fetchUsersOnProjectStats`,
  async (accountId: string, thunkAPI) => {
    try {
      const response = await axios.get(
        apiEndpoints.userOnProjectStats(accountId),
      );
      return response.data as UsersOnProjectStatsResponse;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);
